import { CheckCircle2, CircleAlert, Copy, Eraser, Ticket } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import type { TomboloState } from '@/hooks/useTombolo';

export function StepNotes({ state }: { state: TomboloState }) {
  const { noteInput, setNoteInput, parsed, noteIds, removeInvalid, setStep, isLocked } = state;

  const validCount = noteIds.length;
  const duplicateCount = parsed.filter((p) => p.status === 'duplicate').length;
  const invalidEntries = parsed.filter((p) => p.status === 'invalid');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-primary" aria-hidden />
          Which notes count for this raffle?
        </CardTitle>
        <CardDescription className="text-base">
          Paste one or more note IDs. You can separate them with new lines, spaces,
          commas or semicolons. Formats that work: <code className="text-xs bg-muted px-1 py-0.5 rounded">note1…</code>,{' '}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">nevent1…</code> or a raw hex ID.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={noteInput}
          onChange={(e) => setNoteInput(e.target.value)}
          disabled={isLocked}
          placeholder={'note1abc…\nnevent1xyz…\nor a 64-character hex ID'}
          rows={5}
          className="font-mono text-sm resize-y"
          aria-label="Note IDs"
        />

        {parsed.length > 0 && (
          <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
            <p className="text-sm font-medium">What we found</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{parsed.length} entered</Badge>
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">
                {validCount} valid
              </Badge>
              {duplicateCount > 0 && (
                <Badge variant="outline">{duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''} removed</Badge>
              )}
              {invalidEntries.length > 0 && (
                <Badge variant="destructive">{invalidEntries.length} need fixing</Badge>
              )}
            </div>

            {/* Valid list */}
            {validCount > 0 && (
              <ul className="space-y-1">
                {parsed.filter((p) => p.status === 'valid').map((p) => (
                  <li key={p.hex} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" aria-hidden />
                    <span className="font-mono text-xs truncate text-muted-foreground">{p.raw}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Duplicates */}
            {duplicateCount > 0 && (
              <ul className="space-y-1">
                {parsed.filter((p) => p.status === 'duplicate').map((p, i) => (
                  <li key={`${p.hex}-${i}`} className="flex items-center gap-2 text-sm">
                    <Copy className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
                    <span className="font-mono text-xs truncate text-muted-foreground line-through">{p.raw}</span>
                    <span className="text-xs text-muted-foreground">duplicate — counted once</span>
                  </li>
                ))}
              </ul>
            )}

            {/* Invalid */}
            {invalidEntries.length > 0 && (
              <div className="space-y-2">
                <ul className="space-y-1">
                  {invalidEntries.map((p, i) => (
                    <li key={`${p.raw}-${i}`} className="flex items-start gap-2 text-sm">
                      <CircleAlert className="h-4 w-4 text-destructive shrink-0 mt-0.5" aria-hidden />
                      <div className="min-w-0">
                        <span className="font-mono text-xs break-all">{p.raw}</span>
                        <p className="text-xs text-destructive">{p.error}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                {!isLocked && (
                  <Button variant="outline" size="sm" onClick={removeInvalid}>
                    <Eraser className="h-4 w-4 mr-1" aria-hidden />
                    Remove invalid entries
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            size="lg"
            disabled={validCount === 0}
            onClick={() => setStep('rules')}
          >
            Continue with {validCount} note{validCount === 1 ? '' : 's'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
